// Komfy Spin-to-Win popup
// Gates (enabled, template, URL, device, country, frequency) → load Klaviyo.js →
// attach trigger (delay / exit-intent / scroll) → 3-stage flow with localStorage resume.

(function () {
  'use strict';

  const STATE_KEY = 'komfy-spin-state';
  const GEO_CACHE_KEY = 'komfy-spin-geo';
  const ATC_KEY = 'komfy-atc-clicked';
  const KLAVIYO_REVISION = '2024-10-15';

  const dataEl = document.getElementById('komfy-spin-data');
  const root = document.getElementById('komfy-spin-root');
  const dialog = document.getElementById('komfy-spin-dialog');
  if (!dataEl || !root || !dialog) return;

  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.warn('[komfy-spin] bad config JSON', err);
    return;
  }
  if (!config.wedges || !config.wedges.length) return;

  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- State ----------
  const readState = () => {
    try { return JSON.parse(localStorage.getItem(STATE_KEY) || '{}') || {}; }
    catch { return {}; }
  };
  const writeState = (patch) => {
    const next = Object.assign({}, readState(), patch, { timestamp: Date.now() });
    try { localStorage.setItem(STATE_KEY, JSON.stringify(next)); } catch {}
  };

  const FREQ_MS = {
    session: 0, // handled via sessionStorage sentinel
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000
  };

  const SESSION_SEEN_KEY = 'komfy-spin-seen-session';

  const isFrequencyBlocked = () => {
    const state = readState();
    // Completed (stage 3) — use main frequency cap
    if (state.stage === 3) {
      if (config.frequency === 'never_again_after_spin') return true;
      if (config.frequency === 'session') return sessionStorage.getItem(SESSION_SEEN_KEY) === '1';
      const cap = FREQ_MS[config.frequency];
      if (cap && state.timestamp && (Date.now() - state.timestamp) < cap) return true;
    }
    // Dismissed at any non-final stage — use dismiss frequency cap against dismissedAt
    if (state.dismissed && state.stage !== 3) {
      if (config.dismissFrequency === 'session') return sessionStorage.getItem(SESSION_SEEN_KEY) === '1';
      const cap = FREQ_MS[config.dismissFrequency];
      const ts = state.dismissedAt || state.timestamp;
      if (cap && ts && (Date.now() - ts) < cap) return true;
    }
    return false;
  };

  // ---------- Gates ----------
  const templateMatches = () => {
    const t = config.template || '';
    switch (config.pages) {
      case 'all': return true;
      case 'home_only': return t === 'index';
      case 'home_and_collections': return t === 'index' || t.startsWith('collection');
      case 'product_only': return t.startsWith('product');
      case 'exclude_cart_checkout': return t !== 'cart' && !t.startsWith('checkout');
      default: return true;
    }
  };

  const urlExcluded = () => {
    if (!config.excludeUrls) return false;
    const path = window.location.pathname;
    return config.excludeUrls
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean)
      .some(p => path === p || path.startsWith(p + '/'));
  };

  const deviceMatches = () => {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (config.devices === 'desktop_only') return !isMobile;
    if (config.devices === 'mobile_only') return isMobile;
    return true;
  };

  const resolveCountry = async () => {
    const shopifyCountry = (window.Shopify && window.Shopify.country) || '';
    if (shopifyCountry) return String(shopifyCountry).toUpperCase();
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) return cached;
    try {
      const res = await fetch('https://ipapi.co/country/', { cache: 'force-cache' });
      if (!res.ok) return '';
      const code = (await res.text()).trim().toUpperCase();
      if (code && code.length === 2) sessionStorage.setItem(GEO_CACHE_KEY, code);
      return code;
    } catch {
      return '';
    }
  };

  const atcExcluded = () => {
    if (!config.excludeAfterAtc) return false;
    return sessionStorage.getItem(ATC_KEY) === '1';
  };

  const installAtcWatchers = () => {
    const flagAtc = () => {
      try { sessionStorage.setItem(ATC_KEY, '1'); } catch (e) {}
    };

    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('button[name="add"], [data-add-to-cart], .product-form__submit, [name="add-to-cart"]')) {
        flagAtc();
      }
    }, { capture: true });

    ['cart:add', 'cart:added', 'cart:refresh', 'cart:update', 'cart:updated'].forEach(evt => {
      document.addEventListener(evt, flagAtc);
    });

    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (...args) {
        const url = (args[0] && (args[0].url || args[0])) || '';
        const isAtc = typeof url === 'string' && /\/cart\/add(\.js|\.json)?(\?|$)/.test(url);
        const promise = origFetch.apply(this, args);
        if (isAtc) promise.then(res => { if (res && res.ok) flagAtc(); }).catch(() => {});
        return promise;
      };
    }
  };

  const countryAllowed = async () => {
    const allowed = (config.allowedCountries || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    const blocked = (config.blockedCountries || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    if (!allowed.length && !blocked.length) return true;
    const code = await resolveCountry();
    if (!code) return true; // fail-open if we can't resolve
    if (blocked.length && blocked.includes(code)) return false;
    if (allowed.length && !allowed.includes(code)) return false;
    return true;
  };

  // ---------- Klaviyo ----------

  let klaviyoLoaded = false;
  const loadKlaviyo = () => {
    if (klaviyoLoaded || !config.publicKey) {
      return;
    }
    klaviyoLoaded = true;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${encodeURIComponent(config.publicKey)}`;
    document.head.appendChild(s);
  };

  const pendingEvents = [];

  const eventNameFor = (type) =>
      type === 'open' ? 'Komfy Spin Viewed'
    : type === 'submit' ? 'Komfy Spin Submitted'
    : type === 'close' ? 'Komfy Spin Closed'
    : null;

  const postClientEvent = async (name, payload, email) => {
    if (!config.publicKey || !email) return { ok: false, skipped: true };
    try {
      const res = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(config.publicKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: payload,
              metric: { data: { type: 'metric', attributes: { name } } },
              profile: { data: { type: 'profile', attributes: { email } } }
            }
          }
        })
      });
      return { ok: res.ok };
    } catch (err) {
      return { ok: false };
    }
  };

  const flushPendingEvents = async (email) => {
    if (!email) return;
    while (pendingEvents.length) {
      const ev = pendingEvents.shift();
      await postClientEvent(ev.name, ev.payload, email);
    }
  };

  const publishShopify = (name, data) => {
    try {
      const api = window.Shopify && window.Shopify.analytics;
      if (api && typeof api.publish === 'function') {
        const payload = Object.assign({ form_id: config.formId || '', template: config.template || '' }, data || {});
        api.publish(name, payload);
      }
    } catch (err) {}
  };

  const dispatchKlaviyoFormEvent = (type, extra) => {
    if (!config.formId) return;

    try {
      const detail = {
        type,
        formId: config.formId,
        companyId: config.publicKey || '',
        metaData: extra && extra.email ? { email: extra.email } : {}
      };
      window.dispatchEvent(new CustomEvent('klaviyoForms', { detail }));
    } catch (err) {}

    const trackName = eventNameFor(type);
    if (!trackName) return;
    const payload = Object.assign({ FormID: config.formId, $source: 'Komfy Spin to Win' }, extra || {});

    try {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(trackName, payload);
      } else {
        window._learnq = window._learnq || [];
        window._learnq.push(['track', trackName, payload]);
      }
    } catch (err) {}

    const email = (extra && extra.email) || null;
    if (email) {
      postClientEvent(trackName, payload, email);
      flushPendingEvents(email);
    } else {
      pendingEvents.push({ name: trackName, payload });
    }
  };

  const collectContext = async () => {
    const url = new URL(window.location.href);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => {
      const v = url.searchParams.get(k);
      if (v) utm[k] = v;
    });
    let country = '';
    try { country = await resolveCountry(); } catch {}
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    return {
      country,
      landing_page: window.location.pathname + window.location.search,
      landing_url: window.location.href,
      referrer: document.referrer || '',
      device: isMobile ? 'mobile' : 'desktop',
      language: (navigator.language || '').slice(0, 10),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      page_template: config.template || '',
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      submitted_at: new Date().toISOString(),
      ...utm
    };
  };

  const klaviyoSubscribe = async (email, prize) => {
    const ctx = await collectContext();
    const properties = {
      komfy_spin_prize: prize.label,
      komfy_spin_code: prize.code || '',
      komfy_spin_reward: prize.rewardType,
      ...ctx
    };

    try {
      const idPayload = {
        $email: email,
        $country: ctx.country,
        ...properties
      };
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify(idPayload);
      } else {
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', idPayload]);
      }
    } catch (err) {}

    if (!config.publicKey || !config.listId) return { ok: false, skipped: true };
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(config.publicKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Komfy Spin to Win',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    location: ctx.country ? { country: ctx.country } : undefined,
                    properties
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: config.listId } }
            }
          }
        })
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        console.warn('[komfy-spin] klaviyo subscribe failed', res.status, body);
      }
      return { ok: res.ok };
    } catch (err) {
      console.warn('[komfy-spin] klaviyo subscribe error', err);
      return { ok: false };
    }
  };

  // ---------- Wheel ----------
  const totalWeight = config.wedges.reduce((s, w) => s + Math.max(1, +w.weight || 1), 0);

  const pickWinnerIndex = () => {
    const r = Math.random() * totalWeight;
    let acc = 0;
    for (let i = 0; i < config.wedges.length; i++) {
      acc += Math.max(1, +config.wedges[i].weight || 1);
      if (r < acc) return i;
    }
    return config.wedges.length - 1;
  };

  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const renderWheel = () => {
    const svg = dialog.querySelector('[data-wheel]');
    if (!svg) return;
    const n = config.wedges.length;
    const anglePer = 360 / n;
    const r = 100;
    const textR = 65;

    const svgNS = 'http://www.w3.org/2000/svg';
    svg.innerHTML = '';

    config.wedges.forEach((w, i) => {
      const start = i * anglePer;
      const end = start + anglePer;
      const p1 = polarToCartesian(0, 0, r, start);
      const p2 = polarToCartesian(0, 0, r, end);
      const large = anglePer > 180 ? 1 : 0;
      const d = `M 0 0 L ${p1.x.toFixed(3)} ${p1.y.toFixed(3)} A ${r} ${r} 0 ${large} 1 ${p2.x.toFixed(3)} ${p2.y.toFixed(3)} Z`;

      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d', d);
      path.setAttribute('fill', w.color || '#7575f2');
      path.setAttribute('stroke', 'rgba(255,255,255,0.55)');
      path.setAttribute('stroke-width', '1.2');
      svg.appendChild(path);

      const mid = start + anglePer / 2;
      const tp = polarToCartesian(0, 0, textR, mid);
      const text = document.createElementNS(svgNS, 'text');
      text.setAttribute('x', tp.x.toFixed(2));
      text.setAttribute('y', tp.y.toFixed(2));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      text.setAttribute('transform', `rotate(${mid} ${tp.x.toFixed(2)} ${tp.y.toFixed(2)})`);
      text.setAttribute('fill', 'var(--komfy-spin-wheel-text, #ffffff)');
      text.setAttribute('font-size', n > 6 ? '18' : '22');
      text.setAttribute('font-weight', '700');
      text.setAttribute('font-family', "'Fredoka', 'Helvetica Neue', Arial, sans-serif");

      // Render "%" in a lighter weight than the rest of the label
      const label = w.label || '';
      const pctIdx = label.indexOf('%');
      if (pctIdx >= 0) {
        const before = label.slice(0, pctIdx);
        const after = label.slice(pctIdx + 1);
        if (before) {
          const t1 = document.createElementNS(svgNS, 'tspan');
          t1.textContent = before;
          text.appendChild(t1);
        }
        const pct = document.createElementNS(svgNS, 'tspan');
        pct.setAttribute('font-weight', '500');
        pct.setAttribute('font-size', '0.7em');
        pct.textContent = '%';
        text.appendChild(pct);
        if (after) {
          const t2 = document.createElementNS(svgNS, 'tspan');
          t2.textContent = after;
          text.appendChild(t2);
        }
      } else {
        text.textContent = label;
      }
      svg.appendChild(text);
    });
  };

  const spinToIndex = (idx) => {
    return new Promise((resolve) => {
      const svg = dialog.querySelector('[data-wheel]');
      const wrap = dialog.querySelector('.komfy-spin__wheel-wrap');
      if (!svg) { resolve(); return; }
      const n = config.wedges.length;
      const anglePer = 360 / n;
      const center = (idx + 0.5) * anglePer;
      const pointerOffset = 22.5;
      const jitter = (Math.random() - 0.5) * (anglePer * 0.6);
      const finalAngle = pointerOffset - center + jitter;
      const turns = 6;
      const totalRotation = 360 * turns + finalAngle;

      if (reduced) {
        svg.style.transform = `rotate(${finalAngle}deg)`;
        resolve();
        return;
      }

      // Reset then animate
      svg.setAttribute('data-spinning', 'false');
      svg.style.transform = 'rotate(0deg)';
      void svg.offsetWidth;
      svg.setAttribute('data-spinning', 'true');
      svg.style.transform = `rotate(${totalRotation}deg)`;

      // Physics-driven pointer: spring returns it to 0°, each wedge-boundary crossing applies an impulse.
      // At high crossing rates, impulses stack faster than the spring can restore — pointer stays deflected.
      const pointerEl = wrap && wrap.querySelector('.komfy-spin__pointer');
      let pAngle = 0, pVel = 0, lastT = null;
      let lastWedge = null;
      const readWheelAngle = () => {
        const style = getComputedStyle(svg);
        const t = style.transform;
        if (!t || t === 'none') return 0;
        try {
          const m = new DOMMatrixReadOnly(t);
          return Math.atan2(m.b, m.a) * 180 / Math.PI;
        } catch { return 0; }
      };
      const physicsStep = (now) => {
        if (lastT == null) { lastT = now; return; }
        const dt = Math.min(0.033, (now - lastT) / 1000);
        lastT = now;
        const k = 900;     // spring stiffness (how fast it returns to 0)
        const damping = 14; // reduces oscillation
        const force = -k * pAngle - damping * pVel;
        pVel += force * dt;
        pAngle += pVel * dt;
        // Clamp to keep visual sane
        if (pAngle < -32) { pAngle = -32; if (pVel < 0) pVel = 0; }
        if (pAngle > 3)   { pAngle = 3;   if (pVel > 0) pVel = 0; }
        if (pointerEl) pointerEl.style.transform = `translateX(-50%) rotate(${pAngle.toFixed(2)}deg)`;
      };
      const tickLoop = (now) => {
        if (svg.getAttribute('data-spinning') === 'true') {
          const ang = readWheelAngle();
          const rel = ((pointerOffset - ang) % 360 + 360) % 360;
          const currentWedge = Math.floor(rel / anglePer);
          if (lastWedge !== null && currentWedge !== lastWedge) {
            pVel -= 160; // impulse CCW (tip kicked back)
          }
          lastWedge = currentWedge;
        }
        physicsStep(now);
        // Keep running until wheel stops AND pointer has settled
        if (svg.getAttribute('data-spinning') === 'true' ||
            Math.abs(pAngle) > 0.3 || Math.abs(pVel) > 0.3) {
          requestAnimationFrame(tickLoop);
        } else if (pointerEl) {
          pointerEl.style.transform = '';
        }
      };
      requestAnimationFrame(tickLoop);

      const onEnd = () => {
        svg.removeEventListener('transitionend', onEnd);
        svg.setAttribute('data-spinning', 'false');
        resolve();
      };
      svg.addEventListener('transitionend', onEnd);
      setTimeout(onEnd, 5200);
    });
  };

  // ---------- Stage control ----------
  const showStage = (n) => {
    dialog.querySelectorAll('.komfy-spin__stage').forEach(el => {
      el.hidden = Number(el.dataset.stage) !== n;
    });
  };

  const openModal = () => {
    sessionStorage.setItem(SESSION_SEEN_KEY, '1');
    const wasOpen = dialog.open;
    if (typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
    } else if (!dialog.open) {
      dialog.setAttribute('open', '');
    }
    document.documentElement.style.overflow = 'hidden';
    if (!wasOpen) {
      dispatchKlaviyoFormEvent('open');
      publishShopify('komfy:spin_viewed');
    }
  };

  const closeModal = (reason) => {
    const wasOpen = dialog.open;
    const currentStage = Number(dialog.querySelector('.komfy-spin__stage:not([hidden])')?.dataset.stage) || 1;
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.documentElement.style.overflow = '';

    if (reason === 'dismiss') {
      writeState({ dismissed: true, dismissedAt: Date.now() });
    }
    if (wasOpen) {
      dispatchKlaviyoFormEvent('close');
      publishShopify('komfy:spin_closed', { stage: currentStage, reason: reason || 'close' });
    }
  };

  // ---------- Bootstrap ----------
  const init = async () => {
    installAtcWatchers();

    if (!config.enabled && config.enabled !== undefined) {/* undefined = allow */}
    if (!templateMatches()) return;
    if (urlExcluded()) return;
    if (!deviceMatches()) return;
    if (atcExcluded()) return;
    if (isFrequencyBlocked()) return;
    if (!(await countryAllowed())) return;

    loadKlaviyo();
    renderWheel();
    attachHandlers();
    showStage(1);
    attachTrigger();
  };

  const attachTrigger = () => {
    const triggers = config.triggers || {};
    const cleanups = [];
    const fire = () => { detachTrigger(); openModal(); };
    detachTrigger = () => {
      while (cleanups.length) {
        try { cleanups.pop()(); } catch (e) {}
      }
    };

    if (triggers.delay) {
      const t = setTimeout(fire, Math.max(1000, (config.delaySeconds || 8) * 1000));
      cleanups.push(() => clearTimeout(t));
    }

    if (triggers.exitIntent) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (!isMobile) {
        const handler = (e) => { if (e.clientY <= 0) fire(); };
        document.addEventListener('mouseleave', handler);
        cleanups.push(() => document.removeEventListener('mouseleave', handler));
      }
    }

    if (triggers.scrollDepth) {
      const pct = Math.max(10, Math.min(90, config.scrollPercent || 50));
      const handler = () => {
        const scrolled = window.scrollY + window.innerHeight;
        const total = document.documentElement.scrollHeight;
        if (total > 0 && (scrolled / total) * 100 >= pct) fire();
      };
      window.addEventListener('scroll', handler, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', handler));
    }

    if (!cleanups.length) {
      const t = setTimeout(fire, Math.max(1000, (config.delaySeconds || 8) * 1000));
      cleanups.push(() => clearTimeout(t));
    }
  };

  let detachTrigger = () => {};
  let currentPrize = null;

  const prizeDisplayLabel = (prize) => {
    if (!prize) return '';
    const base = prize.label || '';
    if (prize.rewardType === 'percent_off' || prize.rewardType === 'dollar_off') {
      return /off/i.test(base) ? base : `${base} OFF`;
    }
    return base;
  };

  const renderPrizeInto = (el, text) => {
    el.textContent = '';
    // Wrap "%" in a span so it can be styled thinner/smaller
    const parts = text.split(/(%)/);
    parts.forEach(part => {
      if (!part) return;
      if (part === '%') {
        const s = document.createElement('span');
        s.className = 'komfy-spin__prize-percent';
        s.textContent = '%';
        el.appendChild(s);
      } else {
        el.appendChild(document.createTextNode(part));
      }
    });
  };

  const prime = (prize) => {
    currentPrize = prize;
    const display = prizeDisplayLabel(prize);
    dialog.querySelectorAll('[data-prize-label]').forEach(el => { renderPrizeInto(el, display); });
    dialog.querySelectorAll('[data-code]').forEach(el => { el.textContent = prize.code || ''; });

    const cta = dialog.querySelector('[data-continue]');
    if (cta) {
      const redirect = cta.dataset.continueUrl || '/collections/all';
      if (prize.code) {
        cta.setAttribute('href', `/discount/${encodeURIComponent(prize.code)}?redirect=${encodeURIComponent(redirect)}`);
      } else {
        cta.setAttribute('href', redirect);
      }
    }
  };

  const applyDiscountSilently = async (code) => {
    if (!code) return;
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        credentials: 'include',
        redirect: 'follow',
        cache: 'no-store'
      });
    } catch (err) {}
  };

  const handleSpin = async (btn) => {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    btn.setAttribute('aria-disabled', 'true');

    const idx = pickWinnerIndex();
    const w = config.wedges[idx];
    await spinToIndex(idx);

    const prize = {
      label: w.label,
      code: w.code || '',
      rewardType: w.rewardType,
      rewardValue: w.rewardValue || ''
    };
    prime(prize);
    writeState({ stage: 2, prize, email: null });

    publishShopify('komfy:spin_spun', {
      prize_label: prize.label,
      reward_type: prize.rewardType,
      reward_value: prize.rewardValue,
      discount_code: prize.code
    });

    // Small pause for drama
    setTimeout(() => {
      showStage(2);
      btn.removeAttribute('aria-disabled');
      // Only auto-focus the email input on desktop — opening the keyboard on mobile
      // squishes the popup layout.
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (!isMobile) {
        const emailInput = dialog.querySelector('[data-email-input]');
        if (emailInput) setTimeout(() => emailInput.focus({ preventScroll: true }), 150);
      }
    }, reduced ? 0 : 500);
  };

  const handleEmailSubmit = async (form) => {
    const input = form.querySelector('[data-email-input]');
    const errorEl = form.querySelector('[data-email-error]');
    const btn = form.querySelector('[data-email-submit]');
    const labelEl = btn && btn.querySelector('[data-submit-label]');
    const originalLabel = labelEl ? labelEl.textContent : '';

    // Browser handles required/email validation via form attributes;
    // this handler only runs after the form passes native checkValidity().
    const email = (input.value || '').trim();
    if (errorEl) errorEl.hidden = true;

    btn.setAttribute('aria-disabled', 'true');
    if (labelEl) labelEl.textContent = 'Unlocking…';

    await klaviyoSubscribe(email, currentPrize || {});
    dispatchKlaviyoFormEvent('submit', { email });
    publishShopify('komfy:spin_submitted', {
      email,
      prize_label: (currentPrize && currentPrize.label) || '',
      reward_type: (currentPrize && currentPrize.rewardType) || '',
      reward_value: (currentPrize && currentPrize.rewardValue) || '',
      discount_code: (currentPrize && currentPrize.code) || ''
    });
    writeState({ stage: 3, email, prize: currentPrize });

    // Fill stage 3 content
    const echo = dialog.querySelector('[data-email-echo]');
    if (echo) echo.textContent = email;

    btn.removeAttribute('aria-disabled');
    if (labelEl) labelEl.textContent = originalLabel;
    showStage(3);
  };

  const handleCopy = async (btn) => {
    const code = (currentPrize && currentPrize.code) || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = code; document.body.appendChild(ta);
      ta.select(); try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    const labelEl = btn.querySelector('[data-copy-label]');
    if (!labelEl) return;
    const original = labelEl.textContent;
    labelEl.textContent = config.copiedLabel || 'Copied!';
    setTimeout(() => { labelEl.textContent = original; }, 1600);
  };

  const attachHandlers = () => {
    dialog.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const closeBtn = target.closest('[data-action="close"]');
      if (closeBtn) { closeModal('dismiss'); return; }

      const spinBtn = target.closest('[data-action="spin"]');
      if (spinBtn) { handleSpin(spinBtn); return; }

      const copyBtn = target.closest('[data-copy]');
      if (copyBtn) { handleCopy(copyBtn); return; }

      const continueBtn = target.closest('[data-continue]');
      if (continueBtn && !e.metaKey && !e.ctrlKey && e.button !== 1) {
        e.preventDefault();
        const code = (currentPrize && currentPrize.code) || '';
        applyDiscountSilently(code);
        publishShopify('komfy:spin_applied', {
          discount_code: code,
          prize_label: (currentPrize && currentPrize.label) || '',
          reward_type: (currentPrize && currentPrize.rewardType) || ''
        });
        closeModal();
        return;
      }
    });

    const form = dialog.querySelector('[data-email-form]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        handleEmailSubmit(form);
      });
    }

    // Backdrop click to close
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) closeModal('dismiss');
    });
  };

  // Kick off
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
